import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getOrgFilters,
  OrgLevelKey,
  OrgLevelOptions,
  OrgSelection,
  ORG_LEVEL_KEYS,
} from '../api/shipments';

/** Selection when the URL has none: TPT Division (300); every other level "All". */
export const DEFAULT_ORG_SELECTION: OrgSelection = { division: '300' };

// URL value for "All" on a level that has a default, so choosing "All" survives a reload
const ALL = 'all';

const selectionKeyOf = (selection: OrgSelection) =>
  JSON.stringify(ORG_LEVEL_KEYS.map((key) => selection[key] ?? null));

const DEFAULT_SELECTION_KEY = selectionKeyOf(DEFAULT_ORG_SELECTION);

const readSelection = (params: URLSearchParams): OrgSelection => {
  // No org levels in the URL (e.g. opening the page from the menu): use the default
  if (!ORG_LEVEL_KEYS.some((key) => params.has(key))) {
    return { ...DEFAULT_ORG_SELECTION };
  }
  const selection: OrgSelection = {};
  ORG_LEVEL_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value && value !== ALL) selection[key] = value;
  });
  return selection;
};

/** Adds the org selection to a URLSearchParams being rebuilt by a page. */
export const writeOrgSelection = (next: URLSearchParams, selection: OrgSelection) => {
  ORG_LEVEL_KEYS.forEach((key) => {
    const value = selection[key];
    if (value) next.set(key, value);
    else if (DEFAULT_ORG_SELECTION[key]) next.set(key, ALL);
  });
};

/**
 * Org hierarchy filter state for a shipments page.
 * `status` scopes the dropdown options to OUs that have CNs on that page.
 * `orgParams` is the selection to spread into the page's API calls.
 */
export const useOrgFilter = (status: string) => {
  const [searchParams] = useSearchParams();
  const [selection, setSelection] = useState<OrgSelection>(() => readSelection(searchParams));
  const [levels, setLevels] = useState<OrgLevelOptions[]>([]);
  const [loading, setLoading] = useState(false);

  // Stable identity per distinct selection, so effects depending on it don't loop
  const selectionKey = selectionKeyOf(selection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const orgParams = useMemo<OrgSelection>(() => ({ ...selection }), [selectionKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getOrgFilters({ status, ...orgParams })
      .then((response) => {
        if (cancelled) return;
        setLevels(response.levels);
        // The server drops selections that are no longer options; follow it
        const confirmed: OrgSelection = {};
        response.levels.forEach((level) => {
          if (level.selected) confirmed[level.key] = level.selected;
        });
        const changed = ORG_LEVEL_KEYS.some((key) => (confirmed[key] ?? null) !== (orgParams[key] ?? null));
        if (changed) setSelection(confirmed);
      })
      .catch(() => {
        if (!cancelled) setLevels([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, orgParams]);

  /** Select a level (undefined clears it); every level below it is cleared. */
  const setLevel = useCallback((key: OrgLevelKey, value: string | undefined) => {
    setSelection((prev) => {
      const next: OrgSelection = {};
      for (const levelKey of ORG_LEVEL_KEYS) {
        if (levelKey === key) {
          if (value) next[levelKey] = value;
          break;
        }
        if (prev[levelKey]) next[levelKey] = prev[levelKey];
      }
      return next;
    });
  }, []);

  /** Back to the default selection (used by "Reset filter"). */
  const clear = useCallback(() => setSelection({ ...DEFAULT_ORG_SELECTION }), []);

  return {
    levels,
    loading,
    orgParams,
    // Differs from the default, so "Reset filter" only shows when there's something to reset
    isActive: selectionKey !== DEFAULT_SELECTION_KEY,
    setLevel,
    clear,
  };
};
